import React, { useEffect, useState } from 'react';
import { subscriptionsApi, channelsApi, tariffsApi } from '../lib/api';
import { Plus, Users, Search, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const statusConfig = {
  active: { label: 'Активна', class: 'badge-active', icon: CheckCircle },
  pending: { label: 'Ожидает', class: 'badge-pending', icon: Clock },
  expired: { label: 'Истекла', class: 'badge-expired', icon: AlertCircle },
  cancelled: { label: 'Отменена', class: 'badge-cancelled', icon: XCircle },
};

const initialFormState = {
  user_telegram_id: '',
  user_username: '',
  channel_id: '',
  tariff_id: '',
  auto_renew: false,
};

export default function Subscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [channels, setChannels] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterChannel, setFilterChannel] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = async () => {
    try {
      const [subscriptionsRes, channelsRes, tariffsRes] = await Promise.all([
        subscriptionsApi.getAll(),
        channelsApi.getAll(),
        tariffsApi.getAll(),
      ]);
      setSubscriptions(subscriptionsRes.data);
      setChannels(channelsRes.data);
      setTariffs(tariffsRes.data);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.user_telegram_id && !formData.user_username) {
      toast.error('Укажите Telegram ID или @username');
      return;
    }
    setSubmitting(true);

    try {
      // Emergent-style: create + activate with invites in one step
      await subscriptionsApi.create({ ...formData, activate: true });
      toast.success('Подписка выдана');
      setModalOpen(false);
      setFormData(initialFormState);
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Ошибка создания подписки');
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivate = async (subscription) => {
    try {
      await subscriptionsApi.activate(subscription.id);
      toast.success('Подписка активирована');
      fetchData();
    } catch (error) {
      toast.error('Ошибка активации подписки');
    }
  };

  const handleCancel = async (subscription) => {
    if (!window.confirm('Отменить подписку?')) return;

    try {
      await subscriptionsApi.update(subscription.id, { status: 'cancelled' });
      toast.success('Подписка отменена');
      fetchData();
    } catch (error) {
      toast.error('Ошибка отмены подписки');
    }
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    if (filterStatus !== 'all' && sub.status !== filterStatus) return false;
    if (filterChannel !== 'all' && sub.channel_id !== filterChannel) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        sub.user_telegram_id.toLowerCase().includes(query) ||
        (sub.user_username && sub.user_username.toLowerCase().includes(query))
      );
    }
    return true;
  });

  const getChannelName = (channelId) => {
    const channel = channels.find(c => c.id === channelId);
    return channel?.name || 'Неизвестный канал';
  };

  const getTariffName = (tariffId) => {
    const tariff = tariffs.find(t => t.id === tariffId);
    return tariff?.name || 'Неизвестный тариф';
  };

  const channelTariffs = formData.channel_id
    ? tariffs.filter(t => t.channel_id === formData.channel_id || t.package_id)
    : tariffs;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="subscriptions-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Подписки</h1>
          <p className="text-zinc-500">Управление подписками пользователей</p>
        </div>
        <Button 
          onClick={() => setModalOpen(true)} 
          className="glow-primary"
          data-testid="add-subscription-btn"
          disabled={tariffs.length === 0}
        >
          <Plus className="w-4 h-4 mr-2" />
          Добавить подписку
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Поиск по Telegram ID или username..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#121212] border-white/10"
            data-testid="search-subscriptions"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-[#121212] border-white/10">
            <SelectValue placeholder="Статус" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все статусы</SelectItem>
            <SelectItem value="active">Активные</SelectItem>
            <SelectItem value="pending">Ожидающие</SelectItem>
            <SelectItem value="expired">Истекшие</SelectItem>
            <SelectItem value="cancelled">Отмененные</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterChannel} onValueChange={setFilterChannel}>
          <SelectTrigger className="w-48 bg-[#121212] border-white/10">
            <SelectValue placeholder="Канал" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все каналы</SelectItem>
            {channels.map((channel) => (
              <SelectItem key={channel.id} value={channel.id}>
                {channel.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Subscriptions Table */}
      {filteredSubscriptions.length > 0 ? (
        <div className="card">
          <table className="data-table data-table-responsive">
            <thead>
              <tr>
                <th>Пользователь</th>
                <th>Канал</th>
                <th>Тариф</th>
                <th>Статус</th>
                <th>Период</th>
                <th className="text-right">Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptions.map((sub) => {
                const status = statusConfig[sub.status];
                const StatusIcon = status.icon;
                return (
                  <tr key={sub.id} data-testid={`subscription-row-${sub.id}`}>
                    <td data-label="Пользователь">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                          <Users className="w-4 h-4 text-zinc-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">
                            {sub.user_username ? `@${sub.user_username}` : sub.user_telegram_id}
                          </p>
                          <p className="text-xs text-zinc-500 font-mono">{sub.user_telegram_id}</p>
                        </div>
                      </div>
                    </td>
                    <td data-label="Канал" className="text-zinc-300">{getChannelName(sub.channel_id)}</td>
                    <td data-label="Тариф" className="text-zinc-300">{getTariffName(sub.tariff_id)}</td>
                    <td data-label="Статус">
                      <span className={`badge ${status.class} inline-flex items-center gap-1`}>
                        <StatusIcon className="w-3 h-3" />
                        {status.label}
                      </span>
                    </td>
                    <td data-label="Период" className="text-sm text-zinc-400">
                      {sub.status === 'active' || sub.status === 'expired' ? (
                        <>
                          {formatDate(sub.start_date)} — {formatDate(sub.end_date)}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td data-label="Действия">
                      <div className="flex items-center justify-end gap-2">
                        {sub.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => handleActivate(sub)}
                            data-testid={`activate-subscription-${sub.id}`}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Активировать
                          </Button>
                        )}
                        {sub.status === 'active' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                await subscriptionsApi.regenerateInvite(sub.id);
                                toast.success('Ссылка обновлена');
                                fetchData();
                              } catch {
                                toast.error('Не удалось обновить ссылку');
                              }
                            }}
                            data-testid={`regen-invite-${sub.id}`}
                          >
                            Ссылка
                          </Button>
                        )}
                        {(sub.status === 'active' || sub.status === 'pending') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancel(sub)}
                            className="text-red-400 hover:text-red-300"
                            data-testid={`cancel-subscription-${sub.id}`}
                          >
                            <XCircle className="w-3 h-3 mr-1" />
                            Отменить
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <Users className="empty-state-icon" />
            <p>
              {searchQuery || filterStatus !== 'all' || filterChannel !== 'all'
                ? 'Подписки не найдены'
                : 'Подписок пока нет'}
            </p>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10">
          <DialogHeader>
            <DialogTitle>Добавить подписку вручную</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="user_telegram_id">Telegram ID (или оставьте пустым)</Label>
                <Input
                  id="user_telegram_id"
                  value={formData.user_telegram_id}
                  onChange={(e) => setFormData({ ...formData, user_telegram_id: e.target.value })}
                  placeholder="123456789"
                  data-testid="subscription-user-id-input"
                />
              </div>
              <div>
                <Label htmlFor="user_username">Username (без @) — можно вместо ID</Label>
                <Input
                  id="user_username"
                  value={formData.user_username}
                  onChange={(e) => setFormData({ ...formData, user_username: e.target.value.replace(/^@/, '') })}
                  placeholder="username"
                  data-testid="subscription-username-input"
                />
              </div>
              <div>
                <Label htmlFor="channel">Канал</Label>
                <Select
                  value={formData.channel_id}
                  onValueChange={(value) => setFormData({ ...formData, channel_id: value, tariff_id: '' })}
                  required
                >
                  <SelectTrigger className="bg-[#121212] border-white/10" data-testid="subscription-channel-select">
                    <SelectValue placeholder="Выберите канал" />
                  </SelectTrigger>
                  <SelectContent>
                    {channels.map((channel) => (
                      <SelectItem key={channel.id} value={channel.id}>
                        {channel.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="tariff">Тариф</Label>
                <Select
                  value={formData.tariff_id}
                  onValueChange={(value) => setFormData({ ...formData, tariff_id: value })}
                  required
                  disabled={!formData.channel_id}
                >
                  <SelectTrigger className="bg-[#121212] border-white/10" data-testid="subscription-tariff-select">
                    <SelectValue placeholder="Выберите тариф" />
                  </SelectTrigger>
                  <SelectContent>
                    {channelTariffs.map((tariff) => (
                      <SelectItem key={tariff.id} value={tariff.id}>
                        {tariff.name} — {tariff.price}₽
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-subscription-btn">
                {submitting ? 'Создание...' : 'Создать'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
